const ATTRIBUTION_STORAGE_KEY = 'qcl_email_attribution';
const MAX_REF_LENGTH = 4096;
const MAX_VALUE_LENGTH = 256;

type AttributionSource = 'url' | 'stored';

export type QclAttributionContext = {
  qclRef: string | null;
  qclCta: string | null;
  qclCampaignId: string | null;
  qclPartnerSlug: string | null;
  qclMessageType: string | null;
  source: AttributionSource;
  landingUrl: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
};

const hasWindow = (): boolean => typeof window !== 'undefined';

const normalizeValue = (
  value: string | null | undefined,
  maxLength = MAX_VALUE_LENGTH
): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  return normalized ? normalized.slice(0, maxLength) : null;
};

const getFirstParam = (params: URLSearchParams, names: string[], maxLength = MAX_VALUE_LENGTH): string | null => {
  for (const name of names) {
    const value = normalizeValue(params.get(name), maxLength);
    if (value) {
      return value;
    }
  }

  return null;
};

const readStoredAttribution = (): QclAttributionContext | null => {
  if (!hasWindow()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<QclAttributionContext>;
    const qclRef = normalizeValue(parsed.qclRef, MAX_REF_LENGTH);
    const qclCta = normalizeValue(parsed.qclCta);
    const qclCampaignId = normalizeValue(parsed.qclCampaignId);
    const qclPartnerSlug = normalizeValue(parsed.qclPartnerSlug);
    const qclMessageType = normalizeValue(parsed.qclMessageType);
    if (!qclRef && !qclCta && !qclCampaignId && !qclPartnerSlug && !qclMessageType) {
      return null;
    }

    return {
      qclRef,
      qclCta,
      qclCampaignId,
      qclPartnerSlug,
      qclMessageType,
      source: 'stored',
      landingUrl: normalizeValue(parsed.landingUrl, 2048),
      firstSeenAt: normalizeValue(parsed.firstSeenAt),
      lastSeenAt: normalizeValue(parsed.lastSeenAt),
    };
  } catch {
    return null;
  }
};

const writeStoredAttribution = (context: QclAttributionContext): void => {
  if (!hasWindow()) {
    return;
  }

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Ignore storage write failures, such as private browsing restrictions.
  }
};

const readUrlAttribution = (): Omit<QclAttributionContext, 'source' | 'firstSeenAt' | 'lastSeenAt'> | null => {
  if (!hasWindow()) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const qclRef = getFirstParam(params, ['qcl_ref'], MAX_REF_LENGTH);
  const qclCta = getFirstParam(params, ['qcl_cta', 'cta']);
  const qclCampaignId = getFirstParam(params, ['qcl_campaign_id', 'campaign_id']);
  const qclPartnerSlug = getFirstParam(params, ['qcl_partner_slug', 'qcl_partner', 'partner_slug']);
  const qclMessageType = getFirstParam(params, ['qcl_message_type']);

  if (!qclRef && !qclCta && !qclCampaignId && !qclPartnerSlug && !qclMessageType) {
    return null;
  }

  return {
    qclRef,
    qclCta,
    qclCampaignId,
    qclPartnerSlug,
    qclMessageType,
    landingUrl: window.location.href,
  };
};

export const getQclAttributionContext = (): QclAttributionContext | null => {
  const incoming = readUrlAttribution();
  const stored = readStoredAttribution();

  if (!incoming) {
    return stored;
  }

  const now = new Date().toISOString();
  const context: QclAttributionContext = {
    qclRef: incoming.qclRef || stored?.qclRef || null,
    qclCta: incoming.qclCta || stored?.qclCta || null,
    qclCampaignId: incoming.qclCampaignId || stored?.qclCampaignId || null,
    qclPartnerSlug: incoming.qclPartnerSlug || stored?.qclPartnerSlug || null,
    qclMessageType: incoming.qclMessageType || stored?.qclMessageType || null,
    source: 'url',
    landingUrl: incoming.landingUrl || stored?.landingUrl || null,
    firstSeenAt: stored?.firstSeenAt || now,
    lastSeenAt: now,
  };

  writeStoredAttribution(context);
  return context;
};

export const getQclAttributionProperties = (): Record<string, string | boolean> => {
  const context = getQclAttributionContext();
  if (!context) {
    return {};
  }

  const properties: Record<string, string | boolean> = {
    email_attribution_present: true,
    qcl_attribution_source: context.source,
  };

  if (context.qclRef) properties.qcl_ref = context.qclRef;
  if (context.qclCta) properties.qcl_cta = context.qclCta;
  if (context.qclCampaignId) {
    properties.qcl_campaign_id = context.qclCampaignId;
    properties.campaign_id = context.qclCampaignId;
  }
  if (context.qclPartnerSlug) {
    properties.qcl_partner_slug = context.qclPartnerSlug;
    properties.partner_slug = context.qclPartnerSlug;
  }
  if (context.qclMessageType) properties.qcl_message_type = context.qclMessageType;
  if (context.landingUrl) properties.qcl_landing_url = context.landingUrl;
  if (context.firstSeenAt) properties.qcl_attribution_first_seen_at = context.firstSeenAt;
  if (context.lastSeenAt) properties.qcl_attribution_last_seen_at = context.lastSeenAt;

  return properties;
};
